// Featured Makers + Contact Info admin managers appended below (see AdminFeaturedMakers, AdminContactInfo).

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Save, Upload } from "lucide-react";
import { api, formatError, fileUrl } from "@/lib/api";
import { Page } from "@/components/Page";
import { AvatarUploader, MediaUploader } from "@/components/Uploader";

/* ---------------- Banners ---------------- */
export function AdminBanners() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", subtitle: "", image_path: "", link_url: "", order: 0, is_active: true });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/banners").then(r => setItems(r.data)).catch(err => toast.error(formatError(err)));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image_path) { toast.error("Please upload a banner image"); return; }
    setBusy(true);
    try {
      if (editing) await api.put(`/admin/banners/${editing.id}`, form);
      else await api.post("/admin/banners", form);
      toast.success(editing ? "Banner updated" : "Banner added");
      setForm({ title: "", subtitle: "", image_path: "", link_url: "", order: 0, is_active: true });
      setEditing(null); load();
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  const remove = async (b) => { if (!window.confirm("Delete banner?")) return; try { await api.delete(`/admin/banners/${b.id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(formatError(err)); } };
  const edit = (b) => { setEditing(b); setForm({ title: b.title, subtitle: b.subtitle, image_path: b.image_path, link_url: b.link_url || "", order: b.order || 0, is_active: b.is_active }); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <Page title="Home banners" subtitle="Upload rotating hero images shown at the top of the storefront home page.">
      <form className="form-section" onSubmit={submit} style={{ marginBottom: 20 }}>
        <AvatarUploader value={form.image_path} onChange={v => setForm({ ...form, image_path: v })} testId="banner-image" />
        <div className="field-grid">
          <label>Title (optional)<input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="banner-title" /></label>
          <label>Order<input type="number" value={form.order} onChange={e => setForm({ ...form, order: Number(e.target.value) })} data-testid="banner-order" /></label>
        </div>
        <label>Subtitle (optional)<input value={form.subtitle} onChange={e => setForm({ ...form, subtitle: e.target.value })} data-testid="banner-subtitle" /></label>
        <label>Link URL (optional)<input value={form.link_url} onChange={e => setForm({ ...form, link_url: e.target.value })} data-testid="banner-link" placeholder="https://…" /></label>
        <label className="check-line"><input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} data-testid="banner-active" /><span>Show on home page</span></label>
        <div className="form-actions">
          <span className="muted">Recommended: 1600×540 landscape image.</span>
          <button className="primary-btn" disabled={busy} data-testid="banner-save">{editing ? "Update banner" : <><Plus size={13} /> Add banner</>}</button>
        </div>
        {editing && <button type="button" className="text-btn" onClick={() => { setEditing(null); setForm({ title: "", subtitle: "", image_path: "", link_url: "", order: 0, is_active: true }); }}>Cancel</button>}
      </form>

      <div className="store-grid" data-testid="banners-grid">
        {items.map(b => (
          <div className="admin-tile" key={b.id} data-testid={`banner-${b.id}`}>
            {b.image_path && <img src={fileUrl(b.image_path)} alt="" style={{ width: "100%", height: 130, objectFit: "cover", borderRadius: 4 }} />}
            <h4 style={{ marginTop: 10 }}>{b.title || "(no title)"}</h4>
            <p className="muted" style={{ fontSize: 12 }}>{b.subtitle}</p>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <span className={b.is_active ? "pill green" : "pill red"}>{b.is_active ? "Active" : "Hidden"}</span>
              <button className="secondary-btn" onClick={() => edit(b)} data-testid={`banner-edit-${b.id}`}><Pencil size={12} /></button>
              <button className="danger-btn" onClick={() => remove(b)} data-testid={`banner-delete-${b.id}`}><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
        {!items.length && <p className="muted">No banners yet.</p>}
      </div>
    </Page>
  );
}

/* ---------------- Gallery ---------------- */
export function AdminGallery() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ image_path: "", caption: "", order: 0 });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/gallery").then(r => setItems(r.data)).catch(err => toast.error(formatError(err)));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.image_path) { toast.error("Please upload an image"); return; }
    setBusy(true);
    try { await api.post("/admin/gallery", form); toast.success("Added to gallery"); setForm({ image_path: "", caption: "", order: 0 }); load(); }
    catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  const remove = async (g) => { if (!window.confirm("Remove image?")) return; try { await api.delete(`/admin/gallery/${g.id}`); load(); } catch (err) { toast.error(formatError(err)); } };
  return (
    <Page title="Photo gallery" subtitle="Images that scroll across the home page. Latest arrivals stay on the left.">
      <form className="form-section" onSubmit={submit} style={{ marginBottom: 20 }}>
        <AvatarUploader value={form.image_path} onChange={v => setForm({ ...form, image_path: v })} testId="gallery-image" />
        <div className="field-grid">
          <label>Caption (optional)<input value={form.caption} onChange={e => setForm({ ...form, caption: e.target.value })} data-testid="gallery-caption" /></label>
          <label>Order<input type="number" value={form.order} onChange={e => setForm({ ...form, order: Number(e.target.value) })} data-testid="gallery-order" /></label>
        </div>
        <div className="form-actions"><span className="muted">Landscape 640×420 works best.</span>
          <button className="primary-btn" disabled={busy} data-testid="gallery-save"><Plus size={13} /> Add photo</button></div>
      </form>
      <div className="store-grid" data-testid="gallery-grid">
        {items.map(g => (
          <div className="admin-tile" key={g.id} data-testid={`gallery-${g.id}`}>
            <img src={fileUrl(g.image_path)} alt="" style={{ width: "100%", height: 130, objectFit: "cover", borderRadius: 4 }} />
            <p className="muted" style={{ fontSize: 12, margin: "8px 0 6px" }}>{g.caption || "(no caption)"}</p>
            <button className="danger-btn" onClick={() => remove(g)} data-testid={`gallery-delete-${g.id}`}><Trash2 size={12} /> Remove</button>
          </div>
        ))}
        {!items.length && <p className="muted">No photos yet.</p>}
      </div>
    </Page>
  );
}

/* ---------------- Counters / stats ---------------- */
export function AdminCounters() {
  const [f, setF] = useState({ authorised_users_total: 500, crafts: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/counters").then(r => setF({ authorised_users_total: r.data.authorised_users_total, crafts: r.data.crafts || [] })).catch(err => toast.error(formatError(err))); }, []);
  const setCraft = (i, patch) => setF({ ...f, crafts: f.crafts.map((c, idx) => idx === i ? { ...c, ...patch } : c) });
  const addCraft = () => setF({ ...f, crafts: [...f.crafts, { name: "", count: 0, description: "" }] });
  const rmCraft = (i) => setF({ ...f, crafts: f.crafts.filter((_, idx) => idx !== i) });
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.put("/admin/counters", { authorised_users_total: Number(f.authorised_users_total), crafts: f.crafts.map(c => ({ ...c, count: Number(c.count) })) });
      toast.success("Counters updated");
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  return (
    <Page title="Public counters" subtitle="Numbers displayed on the home page, About page, and Authorised Users directory.">
      <form className="form-section" onSubmit={save}>
        <label>Total authorised users (headline number)<input type="number" min="0" value={f.authorised_users_total} onChange={e => setF({ ...f, authorised_users_total: e.target.value })} data-testid="counter-total" required /></label>
        <div style={{ marginTop: 10 }}>
          <div className="form-top">
            <div><p className="eyebrow">CRAFT-WISE COUNTERS</p><h4>Break down by GI craft</h4></div>
            <button type="button" className="secondary-btn" onClick={addCraft} data-testid="add-craft-button"><Plus size={13} /> Add craft</button>
          </div>
          {f.crafts.map((c, i) => (
            <div className="card-inline" key={i} data-testid={`craft-${i}`}>
              <div className="field-grid">
                <label>Craft name<input value={c.name} onChange={e => setCraft(i, { name: e.target.value })} data-testid={`craft-${i}-name`} /></label>
                <label>Count<input type="number" min="0" value={c.count} onChange={e => setCraft(i, { count: e.target.value })} data-testid={`craft-${i}-count`} /></label>
              </div>
              <label>Short description<input value={c.description || ""} onChange={e => setCraft(i, { description: e.target.value })} data-testid={`craft-${i}-desc`} /></label>
              <button type="button" className="danger-btn" onClick={() => rmCraft(i)} data-testid={`craft-${i}-remove`}><Trash2 size={12} /> Remove</button>
            </div>
          ))}
        </div>
        <div className="form-actions">
          <span className="muted">Changes reflect immediately across public pages.</span>
          <button className="primary-btn" disabled={busy} data-testid="counter-save">{busy ? "Saving…" : <>Save counters <Save size={14} /></>}</button>
        </div>
      </form>
    </Page>
  );
}

/* ---------------- About Us content ---------------- */
export function AdminAbout() {
  const [f, setF] = useState({ intro: "", mission: "", vision: "", established_year: "2017", gi_since_year: "2023", hero_photo: "", work_details: [], achievements: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/about").then(r => setF({ ...f, ...r.data })).catch(err => toast.error(formatError(err))); /* eslint-disable-line */ }, []);

  const setWD = (i, patch) => setF({ ...f, work_details: f.work_details.map((x, idx) => idx === i ? { ...x, ...patch } : x) });
  const setAch = (i, patch) => setF({ ...f, achievements: f.achievements.map((x, idx) => idx === i ? { ...x, ...patch } : x) });

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.put("/admin/about", f); toast.success("About page updated"); }
    catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };

  return (
    <Page title="About us content" subtitle="Manage the story shown on the public About Us page.">
      <form className="form-section" onSubmit={save}>
        <AvatarUploader value={f.hero_photo} onChange={v => setF({ ...f, hero_photo: v })} testId="about-hero" />
        <label>Intro / who we are<textarea rows={4} value={f.intro} onChange={e => setF({ ...f, intro: e.target.value })} data-testid="about-intro" style={{ textAlign: "justify" }} /></label>
        <div className="field-grid">
          <label>Established year<input value={f.established_year} onChange={e => setF({ ...f, established_year: e.target.value })} data-testid="about-established" /></label>
          <label>Working for GI since<input value={f.gi_since_year} onChange={e => setF({ ...f, gi_since_year: e.target.value })} data-testid="about-gi-since" /></label>
        </div>
        <label>Mission<textarea rows={3} value={f.mission} onChange={e => setF({ ...f, mission: e.target.value })} data-testid="about-mission" style={{ textAlign: "justify" }} /></label>
        <label>Vision<textarea rows={3} value={f.vision} onChange={e => setF({ ...f, vision: e.target.value })} data-testid="about-vision" style={{ textAlign: "justify" }} /></label>

        <div style={{ marginTop: 12 }}>
          <div className="form-top">
            <div><p className="eyebrow">OUR WORK</p><h4>Work details with photos</h4></div>
            <button type="button" className="secondary-btn" onClick={() => setF({ ...f, work_details: [...f.work_details, { title: "", description: "", photo: "" }] })} data-testid="add-work-button"><Plus size={13} /> Add</button>
          </div>
          {f.work_details.map((w, i) => (
            <div className="card-inline" key={i} data-testid={`work-${i}`}>
              <AvatarUploader value={w.photo} onChange={v => setWD(i, { photo: v })} testId={`work-${i}-photo`} />
              <label>Title<input value={w.title} onChange={e => setWD(i, { title: e.target.value })} data-testid={`work-${i}-title`} /></label>
              <label>Description<textarea value={w.description} onChange={e => setWD(i, { description: e.target.value })} data-testid={`work-${i}-desc`} /></label>
              <button type="button" className="danger-btn" onClick={() => setF({ ...f, work_details: f.work_details.filter((_, idx) => idx !== i) })} data-testid={`work-${i}-remove`}><Trash2 size={12} /> Remove</button>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 12 }}>
          <div className="form-top">
            <div><p className="eyebrow">ACHIEVEMENTS</p><h4>Recognitions with photos</h4></div>
            <button type="button" className="secondary-btn" onClick={() => setF({ ...f, achievements: [...f.achievements, { title: "", description: "", date: "", photo: "" }] })} data-testid="add-tv-ach-button"><Plus size={13} /> Add</button>
          </div>
          {f.achievements.map((a, i) => (
            <div className="card-inline" key={i} data-testid={`tv-ach-${i}`}>
              <AvatarUploader value={a.photo} onChange={v => setAch(i, { photo: v })} testId={`tv-ach-${i}-photo`} />
              <div className="field-grid">
                <label>Title<input value={a.title} onChange={e => setAch(i, { title: e.target.value })} data-testid={`tv-ach-${i}-title`} /></label>
                <label>Date<input type="date" value={a.date || ""} onChange={e => setAch(i, { date: e.target.value })} data-testid={`tv-ach-${i}-date`} /></label>
              </div>
              <label>Description<textarea value={a.description} onChange={e => setAch(i, { description: e.target.value })} data-testid={`tv-ach-${i}-desc`} /></label>
              <button type="button" className="danger-btn" onClick={() => setF({ ...f, achievements: f.achievements.filter((_, idx) => idx !== i) })} data-testid={`tv-ach-${i}-remove`}><Trash2 size={12} /> Remove</button>
            </div>
          ))}
        </div>

        <div className="form-actions"><span className="muted">Public About Us updates instantly.</span>
          <button className="primary-btn" disabled={busy} data-testid="about-save">{busy ? "Saving…" : <>Save about page <Save size={14} /></>}</button></div>
      </form>
    </Page>
  );
}

/* ---------------- Contact messages ---------------- */
export function AdminContact() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/contact").then(r => setItems(r.data)).catch(err => toast.error(formatError(err)));
  useEffect(() => { load(); }, []);
  const mark = async (m, status) => { try { await api.put(`/admin/contact/${m.id}`, { status }); toast.success("Updated"); load(); } catch (err) { toast.error(formatError(err)); } };
  const remove = async (m) => { if (!window.confirm("Delete message?")) return; try { await api.delete(`/admin/contact/${m.id}`); load(); } catch (err) { toast.error(formatError(err)); } };
  return (
    <Page title="Contact messages" subtitle="Messages submitted via the public Contact Us page.">
      <div className="admin-table" data-testid="admin-contact-table">
        <div className="admin-row admin-head"><span>Name</span><span>Email / phone</span><span>Message</span><span>Status</span><span></span></div>
        {items.map(m => (
          <div className="admin-row" key={m.id} data-testid={`contact-msg-${m.id}`}>
            <span>{m.name}</span>
            <span>{m.email}<br /><small className="muted">{m.phone}</small></span>
            <span style={{ whiteSpace: "normal" }}><b>{m.subject}</b><br />{m.message}</span>
            <span>
              <select value={m.status} onChange={e => mark(m, e.target.value)} data-testid={`contact-status-${m.id}`}>
                <option>New</option><option>Read</option><option>Replied</option><option>Closed</option>
              </select>
            </span>
        <button className="danger-btn" onClick={() => remove(m)} data-testid={`contact-delete-${m.id}`} style={{ minWidth: 44 }}><Trash2 size={12} /> Delete</button>
          </div>
        ))}
        {!items.length && <div className="admin-row"><span>No messages yet.</span></div>}
      </div>
    </Page>
  );
}

/* ---------------- Authorised Users directory ---------------- */
const emptyAU = { firm_name: "", proprietor_name: "", craft_name: "", user_registration_no: "", gst_number: "", aadhaar_number: "", msme_number: "", mobile: "", email: "", address: "", item_name: "", photo: "", is_active: true, notes: "" };
export function AdminAuthorisedUsers() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyAU);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const load = () => api.get("/admin/authorised-users").then(r => setItems(r.data)).catch(err => toast.error(formatError(err)));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.proprietor_name) { toast.error("Proprietor name is required"); return; }
    setBusy(true);
    try {
      if (editing) await api.put(`/admin/authorised-users/${editing.id}`, form);
      else await api.post("/admin/authorised-users", form);
      toast.success(editing ? "Record updated" : "Authorised user added");
      setForm(emptyAU); setEditing(null); setShowForm(false); load();
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  const remove = async (u) => { if (!window.confirm(`Delete ${u.proprietor_name}?`)) return; try { await api.delete(`/admin/authorised-users/${u.id}`); load(); } catch (err) { toast.error(formatError(err)); } };
  const edit = (u) => { setEditing(u); setForm({ ...emptyAU, ...u }); setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const fields = [
    ["firm_name", "Name of the firm (if firm)"],
    ["proprietor_name", "Name of the proprietor"],
    ["craft_name", "Craft name (e.g., Sambhal Horn Craft)"],
    ["user_registration_no", "GI Authorised User Registration No."],
    ["gst_number", "GST number"],
    ["aadhaar_number", "Aadhaar number"],
    ["msme_number", "MSME registration"],
    ["mobile", "Mobile number"],
    ["email", "Email address"],
    ["item_name", "Item name"],
  ];
  return (
    <Page title="Authorised users directory" subtitle="Manually register authorised users displayed on the public directory.">
      <div className="toolbar">
        <div><p className="eyebrow">DIRECTORY</p><h3>{items.length} record{items.length === 1 ? "" : "s"}</h3></div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <label className="secondary-btn" data-testid="au-bulk-import-label" style={{ position: "relative", cursor: "pointer" }}>
            <Upload size={14} /> Bulk import CSV
            <input
              type="file" accept=".csv,text/csv" data-testid="au-bulk-import-input"
              style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }}
              onChange={async (e) => {
                const file = e.target.files?.[0]; e.target.value = "";
                if (!file) return;
                const fd = new FormData(); fd.append("file", file);
                try {
                  const r = await api.post("/admin/authorised-users/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  const { added, updated, skipped, errors } = r.data;
                  toast.success(`Added ${added}, updated ${updated}${skipped ? `, skipped ${skipped}` : ""}`);
                  if (errors && errors.length) toast.error(errors.join("; "));
                  load();
                } catch (err) { toast.error(formatError(err)); }
              }}
            />
          </label>
          <button className="primary-btn" onClick={() => { setShowForm(!showForm); if (!showForm) { setEditing(null); setForm(emptyAU); } }} data-testid="au-toggle-form">
            {showForm ? "Close form" : <><Plus size={14} /> Register authorised user</>}
          </button>
        </div>
      </div>
      <p className="muted" data-testid="au-bulk-hint" style={{ marginTop: -8, marginBottom: 12 }}>
        CSV columns supported: <b>name</b>, firm_name, <b>gi_item</b>, state, user_registration_no, phone, email. Name and gi_item are required. Existing rows are updated by user_registration_no or name+gi_item.
      </p>

      {showForm && (
        <form className="form-section" onSubmit={submit} style={{ marginBottom: 20 }}>
          <AvatarUploader value={form.photo} onChange={v => setForm({ ...form, photo: v })} testId="au-photo" />
          <div className="field-grid">
            {fields.map(([n, l]) => (
              <label key={n}>{l}<input value={form[n] || ""} onChange={e => setForm({ ...form, [n]: e.target.value })} data-testid={`au-${n}`} required={n === "proprietor_name"} /></label>
            ))}
          </div>
          <label>Complete address<textarea value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="au-address" /></label>
          <label>Internal notes<textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="au-notes" /></label>
          <label className="check-line"><input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} data-testid="au-active" /><span>Show on public directory</span></label>
          <div className="form-actions">
            <span className="muted">Aadhaar is stored server-side and NEVER exposed on the public directory.</span>
            <button className="primary-btn" disabled={busy} data-testid="au-save">{editing ? "Update record" : "Register user"}</button>
          </div>
        </form>
      )}

      <div className="admin-table" data-testid="admin-au-table">
        <div className="admin-row admin-head"><span>Name</span><span>Craft</span><span>Contact</span><span>Status</span><span></span></div>
        {items.map(u => (
          <div className="admin-row" key={u.id} data-testid={`admin-au-${u.id}`}>
            <span><b>{u.firm_name || u.proprietor_name}</b><br /><small className="muted">{u.proprietor_name}{u.user_registration_no ? ` · Reg. No. ${u.user_registration_no}` : ""}</small></span>
            <span>{u.craft_name}<br /><small className="muted">{u.item_name}</small></span>
            <span>{u.mobile}<br /><small className="muted">{u.email}</small></span>
            <span className={u.is_active ? "pill green" : "pill red"}>{u.is_active ? "Public" : "Hidden"}</span>
            <span style={{ display: "flex", gap: 6 }}>
              <button className="secondary-btn" onClick={() => edit(u)} data-testid={`admin-au-edit-${u.id}`}><Pencil size={12} /></button>
              <button className="danger-btn" onClick={() => remove(u)} data-testid={`admin-au-delete-${u.id}`}><Trash2 size={12} /></button>
            </span>
          </div>
        ))}
        {!items.length && <div className="admin-row"><span>No authorised users yet — register one above.</span></div>}
      </div>
    </Page>
  );
}

/* ---------------- Featured Makers ---------------- */
const emptyMaker = { name: "", place: "", gi: "", initials: "", color: "#0B6B3F", photo: "", story: "", order: 0, is_active: true };
export function AdminFeaturedMakers() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyMaker);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/featured-makers").then(r => setItems(r.data)).catch(err => toast.error(formatError(err)));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      if (editing) await api.put(`/admin/featured-makers/${editing.id}`, form);
      else await api.post("/admin/featured-makers", form);
      toast.success(editing ? "Maker updated" : "Maker added");
      setForm(emptyMaker); setEditing(null); load();
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  const remove = async (m) => { if (!window.confirm(`Delete ${m.name}?`)) return; try { await api.delete(`/admin/featured-makers/${m.id}`); load(); } catch (err) { toast.error(formatError(err)); } };
  const edit = (m) => { setEditing(m); setForm({ ...emptyMaker, ...m }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const activeCount = items.filter(m => m.is_active).length;
  return (
    <Page title="Meet the Makers" subtitle="Curate up to 5 authorised users to feature on the storefront carousel.">
      {activeCount > 5 && <div className="error" data-testid="makers-limit-warning">Only the first 5 active makers (by order) will be shown on the home carousel — currently {activeCount} are active.</div>}
      <form className="form-section" onSubmit={submit} style={{ marginBottom: 20 }}>
        <AvatarUploader value={form.photo} onChange={v => setForm({ ...form, photo: v })} testId="maker-photo" />
        <div className="field-grid">
          <label>Maker / firm name<input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="maker-name" /></label>
          <label>Place<input value={form.place} onChange={e => setForm({ ...form, place: e.target.value })} data-testid="maker-place" placeholder="e.g., Mysuru, Karnataka" /></label>
          <label>GI item<input value={form.gi} onChange={e => setForm({ ...form, gi: e.target.value })} data-testid="maker-gi" placeholder="e.g., Mysore Silk" /></label>
          <label>Initials (2 letters)<input maxLength={2} value={form.initials} onChange={e => setForm({ ...form, initials: e.target.value.toUpperCase() })} data-testid="maker-initials" /></label>
          <label>Accent colour<input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} data-testid="maker-color" /></label>
          <label>Order<input type="number" value={form.order} onChange={e => setForm({ ...form, order: Number(e.target.value) })} data-testid="maker-order" /></label>
        </div>
        <label>Story<textarea rows={3} value={form.story} onChange={e => setForm({ ...form, story: e.target.value })} data-testid="maker-story" /></label>
        <label className="check-line"><input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} data-testid="maker-active" /><span>Show on home carousel</span></label>
        <div className="form-actions">
          <span className="muted">Home carousel picks the first 5 active makers by order.</span>
          <button className="primary-btn" disabled={busy} data-testid="maker-save">{editing ? "Update maker" : <><Plus size={13} /> Add maker</>}</button>
        </div>
        {editing && <button type="button" className="text-btn" onClick={() => { setEditing(null); setForm(emptyMaker); }}>Cancel edit</button>}
      </form>

      <div className="admin-table" data-testid="admin-makers-table">
        <div className="admin-row admin-head"><span>Name</span><span>Place · GI</span><span>Order</span><span>Status</span><span></span></div>
        {items.map(m => (
          <div className="admin-row" key={m.id} data-testid={`maker-${m.id}`}>
            <span><b>{m.name}</b></span>
            <span>{m.place} · {m.gi}</span>
            <span>{m.order}</span>
            <span className={m.is_active ? "pill green" : "pill red"}>{m.is_active ? "Active" : "Hidden"}</span>
            <span style={{ display: "flex", gap: 6 }}>
              <button className="secondary-btn" onClick={() => edit(m)} data-testid={`maker-edit-${m.id}`}><Pencil size={12} /></button>
              <button className="danger-btn" onClick={() => remove(m)} data-testid={`maker-delete-${m.id}`}><Trash2 size={12} /></button>
            </span>
          </div>
        ))}
        {!items.length && <div className="admin-row"><span>No makers yet — add up to 5 for the home carousel.</span></div>}
      </div>
    </Page>
  );
}

/* ---------------- Contact Info ---------------- */
export function AdminContactInfo() {
  const [f, setF] = useState({ address: "", email: "", phone: "", map_url: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/contact-info").then(r => setF({ address: r.data.address || "", email: r.data.email || "", phone: r.data.phone || "", map_url: r.data.map_url || "" })).catch(err => toast.error(formatError(err))); }, []);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.put("/admin/contact-info", f); toast.success("Contact info updated"); }
    catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  return (
    <Page title="Contact info" subtitle="Displayed on the public Contact Us page.">
      <form className="form-section" onSubmit={save}>
        <label>Office address<textarea rows={3} value={f.address} onChange={e => setF({ ...f, address: e.target.value })} data-testid="contact-info-address" /></label>
        <div className="field-grid">
          <label>Email<input type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid="contact-info-email-input" /></label>
          <label>Phone<input value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} data-testid="contact-info-phone-input" /></label>
        </div>
        <label>Map embed URL (optional)<input value={f.map_url} onChange={e => setF({ ...f, map_url: e.target.value })} data-testid="contact-info-map" placeholder="Google Maps embed URL" /></label>
        <div className="form-actions">
          <span className="muted">Public Contact Us page updates instantly.</span>
          <button className="primary-btn" disabled={busy} data-testid="contact-info-save">{busy ? "Saving…" : <>Save contact info <Save size={14} /></>}</button>
        </div>
      </form>
    </Page>
  );
}
